import React, { useEffect, useRef, useState } from 'react'
import classes from './index.module.css'
import Loader from '../../../Components/GeneralComponents/Loader'
import { Wrapper } from '../../../Components/page/Wrapper'
import { Header } from '../../../Components/page/Header'
import { HeaderInfo } from '../../../Components/page/HeaderInfo'
import { Actions } from '../../../Components/page/Actions'
import { ButtonOutline } from '../../../Components/GeneralComponents/ButtonOutline/ButtonOutline'
import { Navigation } from '../../../Components/page/Navigation'

import PageTitle from '../components/PageTitle'
import { useNavigate, useParams } from 'react-router-dom'
import { useGetTeamDetails } from '../hooks/useGetTeamDetails'
import ViewAll from '../components/ViewAll'
import ArtistPreview from '../components/ArtistPreview'
import useVisibleItems from '../hooks/useVisibleItems'
import useAdminAccess from '../../../services/hooks/useAdminAccess'
import { getUserRole } from '../../../services/helpers/getUserRole.'
import { useAuth } from '../../../Contexts/AuthContext'
import { groupRoles, orgRoles } from '../../../constants/roles'
import { useInView } from 'react-intersection-observer'
import { useFetchAccount } from '../../Organization/hooks'
import OrganizationsPopup from '../components/OrganizationsPopup'
import usePopup from '../hooks/usePopup'
import successToast from '../../../Components/Toasts/SuccessToast'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useGetOrgGroupContent } from '../hooks/useGetOrgGroupContent'
import { useGetOrgGroupUsers } from '../hooks/useGetOrgGroupUsers'
import { useGetOrgDetails } from '../hooks/useGetOrgDetails'
import ManageGroupArtistsPopupForm from '../components/ManageGroupArtistsPopupForm'
import AddMembersTeamPopupForm from '../components/AddMembersTeamPopupForm'
import TeamUsersTable from '../components/TeamUsersTable'
import { useChangeOrganizationUsersRole } from '../hooks/useChangeOrganizationUsersRole'
import { renderBreadcrumbs } from './breadcrumbs'
import useAccessLevel from '../hooks/useAccessLevel'
import { ModalWindow } from '../../../Components/GeneralComponents/ModalWindow/ModalWindow'
import ConfirmationForm from '../components/ConfirmationForm'
import OptionsButton from '../../../Components/GeneralComponents/OptionsButton/OptionsButton'
import errorToast from '../../../Components/Toasts/ErrorToast'
import useDeleteOrganizationGroup from '../hooks/useDeleteOrganizationGroup'
import { useAPI } from '../../../Contexts/APIContext'

export default function TeamDetails() {
  const { o_f_id, g_f_id } = useParams()
  const navigate = useNavigate()
  const artistsRef = useRef()
  const { getOrganizationUsers, deleteOrganizationUser, getOrganizationUser } =
    useAPI()
  const [artistRef, artistInView] = useInView()
  const { isInternalAdmin, hasOrgAdminAccess, groupRole } = useAccessLevel({
    o_f_id,
    g_f_id,
  })

  const isTeamAdmin = groupRole && groupRole.includes('Administrator')

  const [users, setUsers] = useState({
    owners: [],
    admin: [],
    viewer: [],
    editor: [],
  })

  const getUsersCount = users => {
    let count = 0

    for (const key in users) {
      if (Object.prototype.hasOwnProperty.call(users, key)) {
        const element = users[key]
        element.length && (count = count + element.length)
      }
    }

    return count
  }

  const {
    openPopup: manageArtistsOpenPopup,
    closePopup: manageArtistsClosePopup,
    isOpen: manageArtistsIsOpen,
  } = usePopup()
  const {
    openPopup: addAdminOpenPopup,
    closePopup: addAdminClosePopup,
    isOpen: addAdminIsOpen,
  } = usePopup()

  const {
    openPopup: deleteTeamOpenPopup,
    closePopup: deleteTeamClosePopup,
    isOpen: deleteTeamPopupIsOpen,
  } = usePopup()

  const deleteOrganizationGroupMutation = useDeleteOrganizationGroup()

  const optionsForManageButton = [
    {
      title: 'DELETE TEAM',
      func: deleteTeamOpenPopup,
    },
  ]

  const handleDeleteTeam = async () => {
    const AllOrgUsers = await getOrganizationUsers({
      o_f_id: o_f_id,
      g_f_id: g_f_id,
    })

    const filteredUsers = AllOrgUsers.organization_users.filter(user =>
      user.organization_groups.some(group => group.f_id === g_f_id)
    )

    filteredUsers.forEach(user => {
      const group = user.organization_groups.find(
        group => group.f_id === g_f_id
      )
      const role = group.roles[0].role

      if (group && user.organization_groups.length === 1) {
        deleteOrganizationUser({ o_f_id: o_f_id, u_f_id: user.f_id })
        return
      }

      const newData = {
        o_f_id: o_f_id,
        g_f_id: g_f_id,
        role: role,
        organization_users: [user.f_id],
        _action: 'delete',
      }

      changeOrganizationUsersRole(newData, {
        onError: error => {
          errorToast('Error deleting User', error)
        },
      })
    })

    deleteOrganizationGroupMutation.mutate({ o_f_id, g_f_id })
    await queryClient.invalidateQueries(['organization'])

    navigate(`/organizations/${o_f_id}`)
  }
  const queryClient = useQueryClient()

  const {
    data: userData,
    isError: userDataError,
    isLoading: userDataIsLoading,
    isFetching: userDataIsFetching,
  } = useFetchAccount()

  const currentUser = userData?.user
  const organization = userData?.organization
  const currentUserRole = getUserRole(currentUser) || ''
  const { hasFullAdminAccess } = useAdminAccess(currentUser)
  const {
    data: orgDetails,
    isError: orgDetailsError,
    isLoading: orgDetailsIsLoading,
    isFetching: orgDetailsFetching,
  } = useGetOrgDetails({
    id: o_f_id,
  })

  const {
    data: organizationGroup,
    isError: organizationGroupError,
    isLoading: organizationGroupIsLoading,
    isFetching: isOrganizationGroupFetching,
  } = useGetTeamDetails({
    o_f_id,
    g_f_id,
  })

  const {
    data: orgOwners,
    isError: orgOwnersError,
    isLoading: orgOwnersIsLoading,
    isFetching: orgOwnersIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Organization Owner',
    enabled: !!organizationGroup && organizationGroup?.name === 'Organization',
  })
  const {
    data: orgAdmins,
    isError: orgAdminError,
    isLoading: orgAdminIsLoading,
    isFetching: orgAdminIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role:
      organizationGroup?.name === 'Organization'
        ? 'Organization Administrator'
        : 'Group Administrator',
    enabled: !!organizationGroup,
  })
  const {
    data: orgEditors,
    isError: orgEditorsError,
    isLoading: orgEditorsIsLoading,
    isFetching: orgEditorsIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Editor',
  })
  const {
    data: orgViewers,
    isError: orgViewersError,
    isLoading: orgViewersIsLoading,
    isFetching: orgViewersIsFetching,
  } = useGetOrgGroupUsers({
    o_f_id,
    g_f_id,
    role: 'Viewer',
  })

  const {
    data: orgContent,
    isError: orgContentError,
    isLoading: orgContentIsLoading,
    isFetching: orgContentIsFetching,
    fetchNextPage: orgContentFetchNextPage,
    hasNextPage: orgContentHasNextPage,
  } = useGetOrgGroupContent({
    o_f_id,
    g_f_id,
    content_object: 'artist',
    n: 15,
  })

  const artists = orgContent?.pages.reduce((acc, page) => {
    return [...acc, ...page.artist].filter(artist => artist.f_id)
  }, [])
  const allArtists = orgContent?.pages[0]?.artist_count || 0

  const { visibleItemsCount: artistsCount } = useVisibleItems({
    ref: artistsRef,
    gap: 15,
    flag: artists,
  })

  const { mutateAsync: changeOrganizationUsersRole } =
    useChangeOrganizationUsersRole()

  const handleDeleteGroupUser = async (data, isChangeRole = false) => {
    const newData = {
      ...data,
      o_f_id,
      g_f_id: data.g_f_id || g_f_id,
      _action: 'delete',
      role:
        data.role === 'Team Administrator' ? 'Group Administrator' : data.role,
    }
    const u_f_id = data.organization_users[0]
    const user = await getOrganizationUser({ o_f_id: o_f_id, u_f_id: u_f_id })
    const groupsCountBeforeDeletion = user.organization_groups.length
    await changeOrganizationUsersRole(newData, {
      onSuccess: () => {
        queryClient.invalidateQueries(['organization'])
        if (isChangeRole) {
          successToast('Access level was successfully updated')
        } else {
          if (groupsCountBeforeDeletion > 1) {
            successToast(
              `The user was removed from this team but still has access to the organization through other ${orgDetails.name} teams or direct Organization membership. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          } else {
            successToast(
              `The user was removed from this team and no longer has access to any artists, but still belongs to the ${orgDetails.name} organization. To fully remove them, an Organization Admin or Owner must take further action.`
            )
          }
        }
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }
  const handleChangeOrganizationUsersRole = data => {
    const { oldRole, ...rest } = data
    const newData = {
      ...rest,
      o_f_id,
      g_f_id: data.g_f_id || g_f_id,
      role:
        rest.role === 'Team Administrator' ? 'Group Administrator' : rest.role,
    }
    changeOrganizationUsersRole(newData, {
      onSuccess: () => {
        handleDeleteGroupUser(
          {
            role:
              oldRole === 'Team Administrator'
                ? 'Group Administrator'
                : oldRole,
            organization_users: newData.organization_users,
            g_f_id: newData.g_f_id,
          },
          true
        )
      },
      onError: error => {
        errorToast('Error updating User', error)
      },
    })
  }

  useEffect(() => {
    if (artistInView && orgContentHasNextPage) {
      orgContentFetchNextPage()
    }
  }, [artistInView, orgContentHasNextPage])

  const [filteredUsers, setFilteredUsers] = useState([])
  const usersCount = filteredUsers.length ?? 0

  useEffect(() => {
    if (!orgAdmins && !orgEditors && !orgViewers) return

    const combineUsers = (org, role) => {
      return (org?.organization_users || []).map(user => ({
        ...user,
        role,
      }))
    }

    const allAdminUsers = combineUsers(orgAdmins, 'Team Administrator')
    const allEditorUsers = combineUsers(orgEditors, 'Editor')
    const allViewerUsers = combineUsers(orgViewers, 'Viewer')

    const sorted = [...allAdminUsers, ...allEditorUsers, ...allViewerUsers]

    setFilteredUsers(sorted)
  }, [orgAdmins, orgEditors, orgViewers])

  if (
    userDataIsLoading ||
    orgDetailsIsLoading ||
    organizationGroupIsLoading ||
    orgAdminIsLoading ||
    orgEditorsIsLoading ||
    orgViewersIsLoading ||
    orgContentIsLoading ||
    orgOwnersIsLoading
  ) {
    return (
      <Loader
        isLoading={
          userDataIsLoading ||
          orgDetailsIsLoading ||
          organizationGroupIsLoading ||
          orgAdminIsLoading ||
          orgEditorsIsLoading ||
          orgViewersIsLoading ||
          orgContentIsLoading ||
          orgOwnersIsLoading
        }
      />
    )
  }

  if (
    userDataError ||
    orgDetailsError ||
    organizationGroupError ||
    orgAdminError ||
    orgEditorsError ||
    orgViewersError ||
    orgContentError ||
    orgOwnersError
  ) {
    return (
      <Wrapper className={classes.wrapper}>
        <Header>
          <HeaderInfo>
            <PageTitle title={'Error loading data'} />
          </HeaderInfo>
        </Header>
      </Wrapper>
    )
  }

  return (
    <Wrapper className={classes.wrapper}>
      <ModalWindow open={deleteTeamPopupIsOpen} onClose={deleteTeamClosePopup}>
        <ConfirmationForm
          title={'Delete Team'}
          message={`Are you sure you want to delete the team ${organizationGroup.name}? This action is permanent and cannot  and cannot be undone.`}
        >
          <ButtonOutline
            text="delete"
            className="upperCase"
            color="red"
            onClick={handleDeleteTeam}
          />
          <ButtonOutline
            text="cancel"
            color="pink"
            className="upperCase"
            onClick={deleteTeamClosePopup}
          />
        </ConfirmationForm>
      </ModalWindow>

      <ManageGroupArtistsPopupForm
        closePopup={manageArtistsClosePopup}
        group={organizationGroup}
        isOpenPopup={manageArtistsIsOpen}
      />
      <OrganizationsPopup
        isOpen={addAdminIsOpen}
        closePopup={addAdminClosePopup}
      >
        <AddMembersTeamPopupForm
          closePopup={addAdminClosePopup}
          group={organizationGroup}
        />
      </OrganizationsPopup>
      <Header>
        {orgDetails && (isInternalAdmin || hasOrgAdminAccess) ? (
          <Navigation breadcrumbs={renderBreadcrumbs(orgDetails)} />
        ) : null}
        <HeaderInfo>
          <div className={classes.header}>
            <PageTitle title={organizationGroup?.name} />
            {orgDetails ? (
              <p className={classes.subtitle}>
                A team within the {orgDetails?.name} organization.
              </p>
            ) : null}
          </div>

          {(isInternalAdmin || hasOrgAdminAccess || isTeamAdmin) && (
            <Actions>
              <ButtonOutline
                color={'pink'}
                size="medium"
                text={'ADD MEMBERS'}
                onClick={addAdminOpenPopup}
                disable={!isInternalAdmin && !hasOrgAdminAccess && !isTeamAdmin}
              />
              <ButtonOutline
                color={'pink'}
                size="medium"
                text={'MANAGE ARTISTS'}
                onClick={manageArtistsOpenPopup}
                disable={!isInternalAdmin && !hasOrgAdminAccess && !isTeamAdmin}
              />
              <div style={{ marginTop: '6px', position: 'relative' }}>
                <OptionsButton
                  disabled={false}
                  options={optionsForManageButton}
                />
              </div>
            </Actions>
          )}
        </HeaderInfo>
      </Header>
      <main className={classes.main}>
        {artists?.length ? (
          <div className={classes.section}>
            <div className={classes.section__header}>
              <h2 className={classes.section__title}>Associated Artists</h2>
              {allArtists > artistsCount ? (
                <ViewAll
                  path={`/organizations/${o_f_id}/teams/${g_f_id}/artists`}
                  count={allArtists}
                />
              ) : null}
            </div>
            <ul className={classes.artists} ref={artistsRef}>
              {artists.map((artist, index) => {
                return (
                  <li
                    ref={artistRef}
                    key={artist.f_id}
                    style={{
                      visibility:
                        index + 1 > artistsCount ? 'hidden' : 'visible',
                      position:
                        index + 1 > artistsCount ? 'absolute' : 'static',
                      top: index + 1 > artistsCount ? '-1000px' : 'unset',
                    }}
                  >
                    <ArtistPreview artist={artist} />
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}
        {users ? (
          <div className={classes.section}>
            <div className={classes.section__header}>
              <h2 className={classes.section__title}>Members</h2>
              {usersCount > 0 ? (
                <ViewAll
                  path={`/organizations/${o_f_id}/teams/${g_f_id}/members`}
                  count={usersCount}
                />
              ) : null}
            </div>
            <div className={classes.users}>
              <TeamUsersTable
                isCurrentUserAnAdmin={hasFullAdminAccess}
                currentUserRole={currentUserRole}
                users={filteredUsers}
                getUserRole={getUserRole}
                options={groupRoles}
                onChangeRole={handleChangeOrganizationUsersRole}
                onDelete={handleDeleteGroupUser}
              />
            </div>
          </div>
        ) : null}
      </main>
    </Wrapper>
  )
}
